import React, { useEffect, useMemo, useRef, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ServiceMultiSelect — Phase 2A.
 *
 * Inline multi-select rendered inside the Clients table cell.
 * - Shows the currently-selected services as compact pill chips.
 * - Clicking opens a checkbox dropdown with the 4 selectable services + ALL.
 * - ALL toggles every service; unticking any individual service after ALL
 *   was ticked will untick ALL (derived state).
 * - Every change POSTs to /api/hq/control/clients/{id}/services/sync which
 *   is idempotent and returns the fresh list — parent gets the updated
 *   `service_slots` via `onChange`.
 * - Rows in ACTIVE / PAUSED / COMPLETED / CANCELLED status can NOT be
 *   removed from here — those show a lock badge and require the Services
 *   tab (status transitions). Rows in INTERESTED can be freely toggled.
 */
const CATALOG = [
  { key: "premium_website",     label: "Website",         abbr: "W"   },
  { key: "crm_business_system", label: "CRM",             abbr: "CRM" },
  { key: "landing_page",        label: "Landing Page",    abbr: "LP"  },
  { key: "orange_optimise",     label: "Orange Optimise", abbr: "O"   },
  { key: "orange_care",         label: "OrangeCare",      abbr: "OC"  },
];
const ALL_KEYS = CATALOG.map((c) => c.key);

const STATUS_TONE = {
  interested: { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  active:     { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  paused:     { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
  completed:  { fg: "#2E7CF6", bg: "#F0F7FF", bd: "#D6E7FF" },
  cancelled:  { fg: "#C1350B", bg: "#FFECEC", bd: "#FCE1C7" },
};

function StatusChip({ label, abbr, status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.interested;
  return (
    <span
      data-testid={`service-chip-${status}`}
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-[0.06em] uppercase mr-1"
      style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}
      title={`${label} · ${status}`}
      aria-label={`${label} — ${status}`}
    >
      {abbr}
    </span>
  );
}

export default function ServiceMultiSelect({ businessId, slots, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const currentByKey = useMemo(() => {
    const m = {};
    (slots || []).forEach((s) => { m[s.service_key] = s; });
    return m;
  }, [slots]);

  // Selected = union of all keys currently on the client
  const selectedKeys = Object.keys(currentByKey);
  const allSelected = ALL_KEYS.every((k) => selectedKeys.includes(k));

  const commitSync = async (nextKeys) => {
    setSaving(true);
    try {
      const r = await hqApi.post(
        `/control/clients/${businessId}/services/sync`,
        { keys: nextKeys },
      );
      onChange?.(r.data.items || []);
      const added = r.data.added || [];
      const removed = r.data.removed || [];
      if (added.length || removed.length) {
        const msgs = [];
        if (added.length) msgs.push(`+${added.length} added`);
        if (removed.length) msgs.push(`-${removed.length} removed`);
        toast.success(`Services updated · ${msgs.join(", ")}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update services");
    } finally {
      setSaving(false);
    }
  };

  const toggleKey = (k) => {
    // Locked rows may not be toggled off from here — must use the Services tab.
    const cur = currentByKey[k];
    if (cur && cur.status !== "interested") {
      toast.message(`${labelFor(k)} is ${cur.status}. Change it from the Services tab.`);
      return;
    }
    const next = new Set(selectedKeys);
    if (next.has(k)) next.delete(k); else next.add(k);
    commitSync(Array.from(next));
  };

  const toggleAll = () => {
    if (allSelected) {
      // Remove only the ones that are INTERESTED — protect actives.
      const keep = selectedKeys.filter((k) => {
        const cur = currentByKey[k];
        return cur && cur.status !== "interested";
      });
      commitSync(keep);
    } else {
      commitSync(ALL_KEYS);
    }
  };

  const labelFor = (k) => CATALOG.find((c) => c.key === k)?.label || k;
  const abbrFor  = (k) => CATALOG.find((c) => c.key === k)?.abbr  || k;

  return (
    <div ref={ref} className="relative" data-testid={`service-multi-${businessId}`}>
      <div className="flex flex-nowrap items-center gap-1">
        {selectedKeys.length === 0 ? (
          <span className="text-[#8A8A8A] text-[11px] italic mr-1 whitespace-nowrap">No services</span>
        ) : (
          selectedKeys.map((k) => (
            <StatusChip
              key={k}
              label={labelFor(k)}
              abbr={abbrFor(k)}
              status={currentByKey[k].status}
            />
          ))
        )}
        <button
          data-testid={`service-multi-toggle-${businessId}`}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          aria-label="Manage services"
          title="Manage services"
          className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#EAEAEA] text-[#4A4A4A] hover:border-[#FF6A00] hover:text-[#FF6A00]"
        >
          {saving ? <Loader2 size={11} className="animate-spin"/> : <ChevronDown size={11}/>}
        </button>
      </div>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-30 mt-2 w-64 rounded-xl border border-[#EAEAEA] bg-white shadow-xl p-2"
          data-testid={`service-multi-panel-${businessId}`}
        >
          <button
            data-testid={`service-multi-all-${businessId}`}
            onClick={toggleAll}
            className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-[#FFF7EE] text-left"
          >
            <span className="text-[11px] font-black tracking-[0.12em] uppercase">All services</span>
            <span
              className={`w-4 h-4 rounded border ${allSelected ? "bg-[#FF6A00] border-[#FF6A00]" : "bg-white border-[#B0B0B0]"} flex items-center justify-center`}
            >
              {allSelected && <Check size={11} className="text-white"/>}
            </span>
          </button>
          <div className="my-1 border-t border-[#EAEAEA]"/>
          {CATALOG.map((c) => {
            const cur = currentByKey[c.key];
            const checked = !!cur;
            const locked = cur && cur.status !== "interested";
            return (
              <button
                key={c.key}
                data-testid={`service-multi-opt-${businessId}-${c.key}`}
                onClick={() => toggleKey(c.key)}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-left ${locked ? "opacity-70" : "hover:bg-[#F6F5F2]"}`}
                disabled={saving}
                title={locked ? `${c.label} is ${cur.status} — change from Services tab` : ""}
              >
                <span className="text-sm">
                  {c.label}
                  {locked && (
                    <span className="ml-1 text-[9px] tracking-[0.16em] uppercase text-[#8A8A8A]">
                      · {cur.status}
                    </span>
                  )}
                </span>
                <span
                  className={`w-4 h-4 rounded border ${checked ? "bg-[#FF6A00] border-[#FF6A00]" : "bg-white border-[#B0B0B0]"} flex items-center justify-center`}
                >
                  {checked && <Check size={11} className="text-white"/>}
                </span>
              </button>
            );
          })}
          <div className="mt-2 px-2 py-1 text-[10px] text-[#8A8A8A] leading-tight">
            Selected means interested. Activate from the client's Services tab.
          </div>
        </div>
      )}
    </div>
  );
}
